import { portalText } from "../../i18n/portal.js";
/**
 * What every per-area slice of the console gateway shares (plan-002 RB-03, design §15.11.4
 * IC-2): the gap row's shape, the one error an unbuilt operation raises, and the context an
 * `apiGateway` slice is built with.
 *
 * ── WHY THIS IS ITS OWN MODULE ───────────────────────────────────────────────────────────
 * `gateway.ts` assembles `GATEWAY_GAP` from every slice's `<area>Gap`, so it imports each slice
 * as a VALUE. If a slice imported `UndocumentedOperationError` back from `gateway.ts`, the two
 * would form an import cycle, and whichever module a bundler or a suite happened to load first
 * would decide whether `GATEWAY_GAP` read a slice's array before it was initialised. So the
 * pieces a slice needs at run time live here, below both, and `gateway.ts` re-exports them:
 * every existing `import { UndocumentedOperationError, type GatewayGap } from "./gateway.js"`
 * keeps working and names the same class. A slice imports from `../gateway.js` with
 * `import type` only, which is erased.
 */
import type { ApiResult } from "@wetalk/api-client";
import type { HelpAccountActionRequest } from "@wetalk/help-widget";

import type { AuthSessionView } from "../gateway.js";

/** One console capability the assembled OpenAPI document does not document yet. */
export interface GatewayGap {
  /** The method on `ConsoleGateway` that has nothing to call. */
  readonly method: string;
  /** The `operationId` the API must grow before `apiGateway` can serve it. */
  readonly wants: string;
  /**
   * Which unit owns the route: a plan-002 unit id (`docs/design/project-design.md` §15.11.2),
   * or `plan-003 (…)` for an operation plan-002 deliberately defers. C7, closed by RB-03.
   */
  readonly owner: string;
}

export class UndocumentedOperationError extends Error {
  override readonly name = "UndocumentedOperationError";
  readonly gap: GatewayGap;
  constructor(gap: GatewayGap) {
    super(
      portalText("shell", "the_console_needs_value_which_the_assembled_openapi_docume_5b24eae3", { v0: String(gap.method), v1: String(gap.wants), v2: String(gap.owner) }),
    );
    this.gap = gap;
  }
}

/**
 * Raises {@link UndocumentedOperationError} for a method in THIS slice's register.
 *
 * Synchronous on purpose: an unimplemented operation is a programming fault, not a request that
 * failed, and a rejected promise would let a `.catch(() => [])` turn "never built" into an empty
 * list. A method that is not in the register is refused too, so a slice cannot invent a gap.
 */
export function refuseFrom(register: readonly GatewayGap[], method: string): never {
  const found = register.find((one) => one.method === method);
  if (found === undefined) {
    throw new Error(`"${method}" is not in this slice's gap register; add a row to it first.`);
  }
  throw new UndocumentedOperationError(found);
}

/**
 * What `apiGateway` hands every slice besides the generated client (IC-2).
 *
 * `now` and `formatWhen` exist so a slice renders every `…Text` field from facts, as design
 * §15.5.2 requires, with the same injected clock the rest of the gateway uses.
 */
export interface SliceContext {
  /** Now, from `ApiGatewaySettings.now`. A suite owns it; a slice never reads the machine's clock. */
  readonly now: () => Date;
  /** `conversation-view.ts`'s relative wording ("Today 14:02"), for every `whenText`. */
  readonly formatWhen: (isoTimestamp: string, now: Date) => string;
  /**
   * The public site's origin (`apps/web-public`), configured and required. The opt-out page's
   * link is composed from it (`optOutPageUrl`), never from a guessed hostname.
   */
  readonly publicSiteBaseUrl: string;
  /**
   * Adopts an auth_session body the API just returned: learns its CSRF token (so the next write
   * carries it) and returns the console's view. For the account slice — sign-up, and RB-17's
   * `createAccount` and `switchAuthSessionAccount`, each of which rotates the cookie.
   * Raises `AuthSessionUnscopedError` and `ImpersonationDetailUnavailableError` exactly as the
   * identity methods in `api-gateway.ts` do, because it is the same function.
   */
  readonly adoptAuthSession: (body: unknown, operationId: string) => Promise<AuthSessionView>;
  /**
   * The `Retry-After` seconds of the `429` answered with this request id, taken once (plan-008
   * ST-RV2, `../retry-after.ts`): the generated client's `ApiResult` does not carry the header.
   */
  readonly takeRetryAfterSecond: (requestId: string | null) => number | null;
  /**
   * plan-025 A-WAPP (design §36.10): sends a confirmed help confirm card's request as the person's own, through
   * `../../help/account-action-client.ts` over the same fetch, cookie, CSRF token and scope generation as the client.
   */
  readonly sendHelpAccountAction: (request: HelpAccountActionRequest, helpActionId: string) => Promise<ApiResult>;
}
